import { useMemo, useState } from "react";

import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DescriptionIcon from "@mui/icons-material/Description";

import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useNavigate } from "react-router-dom";

function Chemicals({ chemicals }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const navigate = useNavigate();
  const filteredChemicals = useMemo(
    () =>
      chemicals.filter((chemical) =>
        (chemical.chemicalName + chemical.name + chemical.cost + chemical.category)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ),
    [chemicals, searchQuery]
  );

  const pagedChemicals = filteredChemicals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium">
          Chemical Information
        </MDTypography>
        <TextField
          id="search-chemicals"
          label="Search Chemicals"
          size="medium"
          sx={{
            minWidth: { xs: "100%", sm: 360 },
            "& .MuiInputBase-root": {
              backgroundColor: "rgba(76, 175, 80, 0.08)",
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.2rem",
            },
          }}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setPage(0);
          }}
        />
      </MDBox>
      <MDButton
        variant="contained"
        color="success"
        onClick={() => {
          navigate("/setup/addchem/");
        }}
      >
        <Icon>add</Icon>&nbsp;Add Chemical
      </MDButton>
      <MDBox mt={1}>
        <TableContainer component={Paper}>
          <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead sx={{ display: "table-header-group", padding: 0, borderRadius: 0 }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Cost</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Target</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Spray Rate</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>SDS</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  <span style={{ color: "#31a439", display: "block", fontWeight: "bold" }}>
                    Cropsure ID
                  </span>
                  <span style={{ color: "#0D3B66", display: "block", fontWeight: "bold" }}>
                    Nupoint ID
                  </span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedChemicals.map((chem) => (
                <TableRow
                  key={chem.key}
                  hover
                  cursor="grab"
                  onClick={() => {
                    navigate(`/setup/chem/${chem.key}`, {
                      state: chem,
                    });
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{chem.chemicalName}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {`$${Number(chem.cost).toFixed(2) ?? ""} per ${chem.unit ?? ""}`}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{chem.target}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{chem.category}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {`${chem.qtyPerHectare ?? ""} ${chem.unit ?? ""} per Ha`}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {chem.sdsUrl && (
                      <a
                        href={chem.sdsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open SDS"
                        style={{ color: "inherit" }}
                      >
                        <DescriptionIcon sx={{ cursor: "pointer" }} />
                      </a>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <span style={{ color: "#31a439", display: "block", fontWeight: "bold" }}>
                      {chem.cropSureID ?? ""}
                    </span>
                    <span style={{ color: "#0D3B66", display: "block", fontWeight: "bold" }}>
                      {chem.nupointID ?? ""}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {pagedChemicals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No chemicals found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredChemicals.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </MDBox>
    </MDBox>
  );
}

Chemicals.propTypes = {
  chemicals: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

export default Chemicals;
